import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Plus, Trash2, User, Key } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function UsersTab({ users, onUpdate }) {
  const [showForm, setShowForm] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  const [selectedUserName, setSelectedUserName] = useState('');
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    full_name: '',
    password: '',
    user_type: 'student',
    skill_level: 'beginner'
  });

  const createUser = async () => {
    if (!newUser.username || !newUser.email || !newUser.full_name || !newUser.password) {
      toast.error('Please fill in all fields');
      return;
    }

    if (newUser.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      await axios.post(`${API}/users`, newUser);
      setNewUser({
        username: '',
        email: '',
        full_name: '',
        password: '',
        user_type: 'student',
        skill_level: 'beginner'
      });
      setShowForm(false);
      toast.success('User created!');
      onUpdate();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to create user');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Delete this user and all their data?')) {
      return;
    }

    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success('User deleted');
      onUpdate();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to delete user');
    }
  };

  const resetPassword = async (userId, username) => {
    if (!window.confirm(`Reset password for ${username}? They will need to change it on next login.`)) {
      return;
    }

    try {
      const response = await axios.post(`${API}/auth/admin-reset-password/${userId}`);
      setTempPassword(response.data.temporary_password);
      setSelectedUserName(username);
      setShowPasswordDialog(true);
      toast.success('Password reset successfully!');
      onUpdate();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to reset password');
    }
  };

  return (
    <div className="space-y-6">
      {/* Create User Form */}
      {!showForm ? (
        <Card>
          <CardContent className="pt-6">
            <Button
              onClick={() => setShowForm(true)}
              className="w-full"
              data-testid="show-user-form-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add New User
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Add New User
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  value={newUser.username}
                  onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                  data-testid="username-input"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  data-testid="email-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="full_name">Full Name</Label>
              <Input
                id="full_name"
                value={newUser.full_name}
                onChange={(e) => setNewUser({...newUser, full_name: e.target.value})}
                data-testid="full-name-input"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                placeholder="Minimum 6 characters"
                data-testid="password-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="user_type">User Type</Label>
                <Select
                  value={newUser.user_type}
                  onValueChange={(value) => setNewUser({...newUser, user_type: value})}
                >
                  <SelectTrigger data-testid="user-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="student">Student</SelectItem>
                    <SelectItem value="reporter">Working Reporter</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="skill_level">Skill Level</Label>
                <Select
                  value={newUser.skill_level}
                  onValueChange={(value) => setNewUser({...newUser, skill_level: value})}
                >
                  <SelectTrigger data-testid="skill-level-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="beginner">Beginner</SelectItem>
                    <SelectItem value="intermediate">Intermediate</SelectItem>
                    <SelectItem value="advanced">Advanced</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={createUser} className="flex-1" data-testid="create-user-btn">
                Create User
              </Button>
              <Button variant="outline" onClick={() => setShowForm(false)} data-testid="cancel-user-btn">
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Users List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            All Users ({users.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 max-h-[600px] overflow-y-auto">
            {users.map((user, index) => {
              const bgColor = index % 2 === 0 ? 'bg-blue-50' : 'bg-purple-50';
              return (
                <Card key={user.id} className={bgColor} data-testid={`user-item-${index}`}>
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold" data-testid={`user-name-${index}`}>{user.full_name}</h3>
                          <Badge variant="outline" data-testid={`user-type-${index}`}>{user.user_type}</Badge>
                          <Badge variant="secondary" data-testid={`skill-level-${index}`}>{user.skill_level}</Badge>
                        </div>
                        <p className="text-sm text-gray-600" data-testid={`user-email-${index}`}>@{user.username} • {user.email}</p>
                        <div className="flex gap-2 mt-2">
                          <Badge 
                            className={user.subscription_status === 'active' ? 'bg-green-600' : 'bg-gray-400'}
                            data-testid={`sub-status-${index}`}
                          >
                            {user.subscription_status}
                          </Badge>
                          {user.subscription_plan && (
                            <Badge variant="outline" data-testid={`sub-plan-${index}`}>{user.subscription_plan}</Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => resetPassword(user.id, user.username)}
                          data-testid={`reset-password-btn-${index}`}
                        >
                          <Key className="w-4 h-4 text-blue-500" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteUser(user.id)}
                          data-testid={`delete-user-btn-${index}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            {users.length === 0 && (
              <p className="text-center text-gray-400 py-8" data-testid="no-users-message">No users yet. Add your first user above!</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Password Reset Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Temporary Password Generated</DialogTitle>
            <DialogDescription>
              A temporary password has been generated for <strong>{selectedUserName}</strong>.
              Please share this password with the user. They must change it on their next login.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-gray-100 p-4 rounded-lg border-2 border-gray-300">
              <p className="text-sm text-gray-600 mb-2">Temporary Password:</p>
              <p className="text-2xl font-mono font-bold text-center text-blue-600">
                {tempPassword}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => {
              navigator.clipboard.writeText(tempPassword);
              toast.success('Password copied to clipboard!');
            }}>
              Copy to Clipboard
            </Button>
            <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}